import './ConnectMarketplaceDashboard.scss';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb, BreadcrumbItem, Card } from 'reactstrap';
import { HomeButton } from '../../components/HomeButton/home-button';
import Layout from '../../components/Layout';
import MyEngagement from './MyEngagement/MyEngagement';
import { useStateSelector } from '../../store/selectors';
import Helpers from '../../utils/helper';
import EngagementBuyers from './Buyers/EngagementBuyers';
import { useDispatch } from 'react-redux';
import {
    SellerMarketplaceTab,
    SellerMarketplaceTabs,
} from '../../slices/marketplace/timeline/models/enums/seller-marketplace-tab.enum';
import EngagementTimeline from './EngagementTimeline/EngagementTimeline';
import {
    BuyerSelectionOptionsList,
    NdaTermSelectionOptionsList,
    TimelineActionType,
} from '../../slices/marketplace/timeline/models/timeline-action-type.model';
import SellersEngagementDocuments from './EngagementDocuments/SellersEngagementDocuments/SellersEngagementDocuments';
import NdaTermsPopup from './NdaTermsPopup/NdaTermsPopup';
import NoPageAccessAlert from '../../components/NoPageAccessAlert/NoPageAccessAlert';
import ContactSupportButton from '../ContactSupportButton/ContactSupportButton';
import { SupportContextEnum } from '../../support-portal/models/SupportContext';
import TeaserInfoModal from '../AdminMarketplace/TeaserInfoModal/TeaserInfoModal';
import BuyerSelectionPopup from './BuyerSelectionPopup/BuyerSelectionPopup';
import DeadlineDocket from './DeadlineDocket/DeadlineDocket';
import { useFeatureFlag } from '../../custom-hooks/useFeatureFlag';
import { FeatureFlagsEnum } from '../FeatureFlags/FeatureFlags.enum';
import { DataAndDocsTab } from './DataAndDocsTab/DataAndDocsTab';
import { TimelineEventStatus } from '../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { EngagementProcessStatus } from '../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { TimelineEvent } from '../../slices/marketplace/timeline/models/timeline-event.model';
import { TimelineTempo } from '../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import { TimelineEventSortingService } from '../AdminMarketplace/TimelinePage/TimelineEventSortingService';
import MarketplaceSettings from '../AdminMarketplace/MarketplaceTabs/MarketplaceSettings/MarketplaceSettings';
import { GetTimelineById } from '../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import { MarketplaceCommsHub } from '../AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHub';
import { PaymentValidator } from './PaymentValidator/PaymentValidator';
import { LoadMarketplacePricing } from '../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import EngagementHistoryTable from '../../components/EngagementHistoryTable/EngagementHistoryTable';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import ValueRangeModal from '../Admin/BuyerMarketplace/ValueRangeModal/ValueRangeModal';
import { getMarketplaceCommsHubTabs } from '../AdminMarketplace/MarketplaceTabs/MarketplaceCommsHub/MarketplaceCommsHubTabs';
import { useCurrentMarketplaceParty } from '../../custom-hooks/useCurrentMarketplaceParty';
import { NotificationFlag } from '../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';
import { RefreshNotificationFlags } from '../../slices/notification-flags/refresh-notification-flags';
import { LevelOfService } from '../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { SelectProfile } from '../../slices/profiles-data-slice/thunks/select-profile.thunk';
import { useSignalR } from '../../context/WebsocketProvider';
import { EngagementBuyer } from '../../slices/marketplace/timeline/models/engagement-buyer.model';
import { MarketplaceBuyer } from '../../slices/marketplace/buyer/models/buyer';
import { MarketplaceSellerProfileComponent } from '../../components/MarketplaceSellerProfile/MarketplaceSellerProfileComponent';
import VDRAccess from './VDRAccess/VDRAccess';
import EngagementDataRoomHost from './EngagementDataRoomHost/EngagementDataRoomHost';

const ConnectMarketplaceDashboard = () => {
    const is28Implemented = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcSellers_2_8
    );
    const isFF_MrktplcBuyers_3_7Implemented = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcBuyers_3_7
    );
    const isFF_MrktplcBuyers_3_8Enabled = useFeatureFlag(
        FeatureFlagsEnum.FF_MrktplcBuyers_3_8
    );

    const connection = useSignalR().connection;
    const isConnected = useSignalR().isConnected;

    const axios = useStateSelector((t) => t.core.axios);
    const navigate = useNavigate();
    const dispatch = useDispatch() as any;

    const summary = useStateSelector((state) => state.userSummary.summary);
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );

    const party = useCurrentMarketplaceParty();

    const isAdmin = party === TimelineParty.VetValue;
    const isBroker = party === TimelineParty.Broker;

    const [isNdaTermPopupVisible, setIsNdaTermPopupVisible] = useState(false);
    const [isBuyerSelectionPopupVisible, setIsBuyerSelectionPopupVisible] =
        useState(false);
    const [isTeaserInfoModalVisible, setIsTeaserInfoModalVisible] =
        useState(false);

    const [systemBuyers, setSystemBuyers] = useState<MarketplaceBuyer[]>([]);

    const [activeEventId, setActiveEventId] = useState<string>('');

    const [searchParams, setSearchParams] = useSearchParams({});
    const engagementId = Number(useParams().engagementId);

    const [activeTab, setActiveTab] = useState<number>(
        SellerMarketplaceTab.MyEngagement
    );
    const [highlightItemId, setHighlightItemId] = useState('');
    const [isRangeOfValueVisible, setIsRangeOfValueVisible] = useState(false);
    const [buyerValueRangeInterest, setBuyerValueRangeInterest] = useState<{
        minValue: number;
        maxValue: number;
    } | null>(null);

    const [isEngagementLoading, setIsEngagementLoading] =
        useState<boolean>(true);
    const [isSystemBuyersLoading, setIsSystemBuyersLoading] =
        useState<boolean>(true);

    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [
        engagementHistoryRefreshTrigger,
        setEngagementHistoryRefreshTrigger,
    ] = useState(false);

    const [availableBuyers, setAvailableBuyers] = useState<EngagementBuyer[]>(
        []
    );

    const [
        isPracticeOfCurrentUserAndIsInEngagement,
        setIsPracticeOfCurrentUserAndIsInEngagement,
    ] = useState<boolean>(false);

    const onDisconnect = () => {
        connection?.off('MP_RefreshTimeline');
        connection?.off('MP_RefreshNotificationFlags');
    };

    useEffect(() => {
        dispatch(LoadMarketplacePricing(engagementId));
        return () => {
            onDisconnect();
        };
    }, []);

    const fetchEngagementAndAvailableBuyers = async (
        fetchBuyers: boolean = true,
        noLoader: boolean = true
    ) => {
        if (!noLoader) setIsEngagementLoading(true);
        const timelinePromise = dispatch(
            GetTimelineById({ id: engagementId, isTemplate: false })
        );
        try {
            await Promise.all(
                !fetchBuyers
                    ? [timelinePromise]
                    : [
                          axios
                              .get(
                                  `/api/Marketplace/Engagements/SFAS/${engagementId}/AvailableBuyers`
                              )
                              .then((response) => {
                                  setAvailableBuyers(response.data);
                              }),
                          timelinePromise,
                      ]
            );
        } finally {
            setIsEngagementLoading(false);
        }
    };

    useEffect(() => {
        if (isConnected) {
            connection.on('MP_RefreshTimeline', () => {
                fetchEngagementAndAvailableBuyers(true, false).then();
                dispatch(RefreshNotificationFlags());
            });
            connection.on('MP_RefreshNotificationFlags', () => {
                dispatch(RefreshNotificationFlags());
            });
        }
    }, [isConnected]);

    useEffect(() => {
        if (!isAdmin) {
            setIsPracticeOfCurrentUserAndIsInEngagement(
                summary?.engagements?.some((p: any) => p.id === engagementId)
            );
        }
    }, [summary]);

    useEffect(() => {
        fetchEngagementAndAvailableBuyers(true, false).then();
    }, [engagementId]);

    const fetchSystemBuyers = () => {
        axios
            .get(`api/Marketplace/MarketplaceBuyers/all/${engagementId}`)
            .then((response) => {
                if (response.status === 200) {
                    setSystemBuyers(response.data);
                }
            })
            .finally(() => {
                setIsSystemBuyersLoading(false);
            });
    };

    useEffect(() => {
        if (isAdmin || isBroker) fetchSystemBuyers();
        else setIsSystemBuyersLoading(false);
    }, []);

    useEffect(() => {
        if (currentEngagement && Array.isArray(currentEngagement.events)) {
            setEvents(currentEngagement.events);
        }

        setEngagementHistoryRefreshTrigger((prev) => !prev);

        if (currentEngagement?.engagementProcess?.sellerProfileId) {
            dispatch(
                SelectProfile(
                    currentEngagement?.engagementProcess?.sellerProfileId
                )
            );
        }
    }, [currentEngagement]);

    useEffect(() => {
        const value = searchParams.get('tab');
        const numberValue = Number(value);
        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= tabComponents.length
        ) {
            setActiveTab(numberValue);
        } else {
            updateSearchParams({
                tab: (1).toString(),
            });
        }
    }, [searchParams]);

    const updateSearchParams = (params: { [key: string]: string }) => {
        const newParams: Record<string, string> = {};
        newParams.tab = params.tab ?? activeTab.toString();
        if (params.subtab !== undefined) {
            newParams.subtab = params.subtab;
        }
        setSearchParams(newParams);
    };

    const navigateToDocument = (documentId: string) => {
        setHighlightItemId(documentId);
        navigateToTab(SellerMarketplaceTab.EngagementDocuments);
    };

    const navigateToProject = (projectId: string) => {
        setHighlightItemId(projectId);
        navigateToTab(SellerMarketplaceTab.DataAndDocs);
    };

    const navigateToBuyers = () => {
        navigateToTab(SellerMarketplaceTab.Buyers);
    };

    const teaserEvent = currentEngagement?.events.find(
        (event) =>
            event.linkedAction?.actionType ===
            TimelineActionType.SellerTeaserApproval
    );

    const isEngagementActive = () => {
        if (!currentEngagement || !currentEngagement?.engagementProcess) {
            return false;
        }
        const status = currentEngagement?.engagementProcess?.status;
        return status === EngagementProcessStatus.Current;
    };

    const tabComponents = [
        {
            id: SellerMarketplaceTab.MyEngagement,
            content: () => (
                <MyEngagement
                    engagementProcess={currentEngagement.engagementProcess}
                    levelOfService={currentEngagement.levelOfService}
                    fetchData={() => fetchEngagementAndAvailableBuyers().then()}
                    isAdminView={isAdmin}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.EngagementCode}`,
        },
        {
            id: SellerMarketplaceTab.EngagementDocuments,
            content: () => (
                <SellersEngagementDocuments
                    timelineId={currentEngagement.id}
                    currentEngagement={currentEngagement}
                    preSelectedDocumentId={highlightItemId}
                    resetPreSelectedDocumentId={() => setHighlightItemId(null)}
                    isReadOnly={!isEngagementActive()}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.EngagementDocumentsCode}`,
        },
        {
            id: SellerMarketplaceTab.EngagementTimeline,
            content: () => (
                <EngagementTimeline
                    id={currentEngagement.id}
                    events={events}
                    startDate={currentEngagement.engagementProcess.startDate}
                    setSearchParams={updateSearchParams}
                    engagementData={currentEngagement.engagementData}
                    onEventClick={onEventClick}
                    tempo={currentEngagement.tempo}
                    levelOfService={currentEngagement.levelOfService}
                    fetchData={() => fetchEngagementAndAvailableBuyers().then()}
                    recalculateEventsBasedOnTempo={
                        recalculateEventsBasedOnTempo
                    }
                    isReadOnly={!isEngagementActive()}
                    isBuyerView={false}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.EngagementTimelinePreviewCode}`,
        },
        {
            id: SellerMarketplaceTab.Buyers,
            content: () => (
                <EngagementBuyers
                    currentParty={party}
                    engagementTimeline={currentEngagement}
                    fetchData={fetchEngagementAndAvailableBuyers}
                    navigateToDocument={navigateToDocument}
                    availableBuyers={availableBuyers}
                    systemBuyers={systemBuyers || []}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.BuyersCode}`,
        },
        {
            id: SellerMarketplaceTab.DeadlineDocket,
            content: () => (
                <DeadlineDocket
                    engagementId={currentEngagement.id}
                    engagementStartDate={
                        currentEngagement.engagementProcess.startDate
                    }
                    engagementData={currentEngagement.engagementData}
                    onEventClick={onEventClick}
                    events={currentEngagement.events}
                    dataRequestProjects={
                        currentEngagement.engagementData?.dataRequestProjects
                    }
                    notificationFlagPrefix={`${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.DeadlineDocketCode}`}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.DeadlineDocketCode}`,
        },
        {
            id: SellerMarketplaceTab.DataAndDocs,
            content: () => (
                <DataAndDocsTab
                    engagementId={currentEngagement.id}
                    preSelectedProjectId={highlightItemId}
                    resetPreSelectedProjectId={() => setHighlightItemId(null)}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.DataAndDocsCode}`,
        },
        {
            id: SellerMarketplaceTab.Settings,
            content: () =>
                isAdmin &&
                currentEngagement.levelOfService !==
                    LevelOfService.StrategicFinancialAdvisoryService ? (
                    <MarketplaceSettings
                        isTemplate={false}
                        engagementId={currentEngagement.id}
                        engagementLevelOfService={
                            currentEngagement.levelOfService
                        }
                    />
                ) : (
                    <></>
                ),
        },
        {
            id: SellerMarketplaceTab.CommsHub,
            content: () => (
                <MarketplaceCommsHub
                    notificationFlagPrefix={`${NotificationFlagsCategories.MpCode}`}
                    engagement={currentEngagement}
                    setSearchParams={updateSearchParams}
                />
            ),
            notificationFlag: `${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.CommsHubCode}`,
        },
        {
            id: SellerMarketplaceTab.EngagementHistory,
            content: () => (
                <EngagementHistoryTable
                    engagementId={currentEngagement.id}
                    events={currentEngagement.events}
                    engagementData={currentEngagement.engagementData}
                    viewParty={
                        isAdmin ? TimelineParty.VetValue : TimelineParty.Seller
                    }
                    canNavigateToTransaction={!isAdmin}
                    onEventClick={onEventClick}
                    isReadonly={!isEngagementActive()}
                    refreshTrigger={engagementHistoryRefreshTrigger}
                    buyers={currentEngagement.engagementProcess?.buyers ?? []}
                    onAdminClickBuyerEvent={onAdminClickBuyerEvent}
                    sellerId={currentEngagement.engagementProcess.userId}
                />
            ),
        },
        {
            id: SellerMarketplaceTab.SellerProfile,
            content: () => <MarketplaceSellerProfileComponent />,
        },
        {
            id: SellerMarketplaceTab.DataRoomLayout,
            content: () => <EngagementDataRoomHost />,
        },
        {
            id: SellerMarketplaceTab.VDRAccess,
            content: () => (
                <VDRAccess
                    currentEngagement={currentEngagement}
                    availableBuyers={availableBuyers}
                />
            ),
        },
    ];

    const navigateToTab = (tab: SellerMarketplaceTab) => {
        updateSearchParams({
            tab: Number(tab).toString(),
        });
    };

    const recalculateEventsBasedOnTempo = (tempo: TimelineTempo) => {
        const newEvents = TimelineEventSortingService.sortTimelineEvents(
            currentEngagement.events,
            currentEngagement.levelOfService,
            tempo
        );
        setEvents(newEvents);
    };

    const onEventClick = (eventId: string) => {
        const guid = eventId.replace('event-', '');
        const events = currentEngagement.events;
        const selectedEvent = events.find((x) => x.id == guid);
        const linkedBuyerReviewAction =
            selectedEvent.linkedAction.actionType ===
                TimelineActionType.BuyerReviewDocument &&
            ((!!selectedEvent.linkedAction?.actionReferenceId &&
                !!events.find(
                    (e) =>
                        e.linkedAction &&
                        e.linkedAction?.id ==
                            selectedEvent.linkedAction?.actionReferenceId
                )) ||
                selectedEvent.linkedAction?.referenceItemId);

        if (
            BuyerSelectionOptionsList.some((option) => {
                return option.value === selectedEvent.linkedAction?.actionType;
            })
        ) {
            setActiveEventId(selectedEvent.id);
            setIsBuyerSelectionPopupVisible(true);
        } else if (
            NdaTermSelectionOptionsList.some((option) => {
                return option.value === selectedEvent.linkedAction?.actionType;
            })
        ) {
            setActiveEventId(selectedEvent.id);
            setIsNdaTermPopupVisible(true);
        } else if (
            TimelineActionType.SellerEngagementDocument ===
                selectedEvent.linkedAction?.actionType ||
            linkedBuyerReviewAction
        ) {
            navigateToDocument(selectedEvent.linkedAction.referenceItemId);
        } else if (
            selectedEvent.linkedAction?.actionType ==
            TimelineActionType.SellerTeaserApproval
        ) {
            setIsTeaserInfoModalVisible(true);
        } else if (
            selectedEvent.linkedAction?.actionType ==
            TimelineActionType.SellerDataAndDocumentRequest
        ) {
            navigateToProject(selectedEvent.linkedAction.referenceItemId);
        } else if (
            selectedEvent.linkedAction?.actionType ==
            TimelineActionType.VetValueBuyerSelectionAndTeaserPosting
        ) {
            navigateToBuyers();
        }
    };

    const onAdminClickBuyerEvent = (eventId: string, buyerId: number) => {
        const events = currentEngagement.events;
        const targetEvent = events.find((x) => x.id == eventId);

        const targetBuyer = currentEngagement.engagementProcess?.buyers.find(
            (b) => b.buyerId === buyerId
        );
        const targetActionState = targetBuyer?.actionState.find(
            (f) => f.actionId === targetEvent.linkedAction?.id
        );

        switch (targetEvent.linkedAction?.actionType) {
            case TimelineActionType.SellerBuyerSelectionIdNda:
            case TimelineActionType.SellerCustomSelectDataNdaTerms:
            case TimelineActionType.BuyerUploadDocument:
            case TimelineActionType.BuyerDocumentExecution:
                const targetBuyerDocumentId =
                    targetActionState?.documentDetailsId;
                if (targetBuyerDocumentId) {
                    navigateToDocument(targetBuyerDocumentId);
                }
                return;
            case TimelineActionType.BuyerExpressionOfInterestWithRange:
                if (targetActionState) {
                    setBuyerValueRangeInterest({
                        minValue: targetActionState?.minPrice ?? 0,
                        maxValue: targetActionState?.maxPrice ?? 0,
                    });
                    setIsRangeOfValueVisible(true);
                }
                return;
            case TimelineActionType.BuyerReviewDocument:
                let targetReviewDocumentId =
                    targetEvent.linkedAction?.referenceItemId;
                if (targetEvent.linkedAction?.actionReferenceId) {
                    const targetReferenceEvent = events.find(
                        (e) =>
                            e.linkedAction?.id ===
                            targetEvent.linkedAction?.actionReferenceId
                    );
                    targetReviewDocumentId =
                        targetReferenceEvent.linkedAction?.referenceItemId;
                }

                if (targetReviewDocumentId) {
                    navigateToDocument(targetReviewDocumentId);
                }

                return;
        }
    };

    const renderTabContent = () => {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return activeTabData ? activeTabData.content() : <></>;
    };

    const getTabNotificationFlag = (tab: number) => {
        const targetCode = tabComponents.find((t) => t.id === tab);
        return targetCode ? (
            <>
                <NotificationFlag
                    flagClassName="notification-flag-wrapper"
                    eventCode={targetCode.notificationFlag}
                    includePrefix={true}
                />
            </>
        ) : (
            <></>
        );
    };

    const renderTabs = () => {
        return (
            <Card
                className={`main-card gantt-chart-container marketplace-dynamic-container`}>
                <div className="body_white_box practice-page-tabs m-0 gantt-chart-container marketplace-dynamic-container">
                    <div className="body_inner_content gantt-chart-container marketplace-dynamic-container">
                        <div className="RRT__container">
                            <div className="RRT__tabs body-tabs">
                                {SellerMarketplaceTabs?.filter((x) =>
                                    shouldShowTab(x)
                                ).map((item) => {
                                    return (
                                        <div
                                            key={item.id}
                                            className={
                                                activeTab === item.id
                                                    ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                                    : 'RRT__tab'
                                            }
                                            onClick={() => {
                                                setActiveTab(item.id);
                                                updateSearchParams({
                                                    tab: item.id.toString(),
                                                });
                                                dispatch(
                                                    RefreshNotificationFlags()
                                                );
                                            }}>
                                            {item.label}
                                            {getTabNotificationFlag(item.id)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        {renderTabContent()}
                    </div>
                </div>
            </Card>
        );
    };

    const renderConnectMarketplaceDashboardPage = () => {
        const fteDvms =
            currentEngagement?.engagementProcess?.practices
                .map((practice) => practice.fteDvms)
                .reduce((a, b) => a + b, 0) ?? 0;
        return (
            <div
                className={`marketplace-dynamic-container ${
                    activeTab === SellerMarketplaceTab.EngagementTimeline
                        ? 'marketplace-dashboard-adapt-timeline-preview'
                        : ''
                }`}>
                {isEngagementLoading || isSystemBuyersLoading ? (
                    Helpers.renderPageLoader()
                ) : (currentEngagement &&
                      isPracticeOfCurrentUserAndIsInEngagement &&
                      !isAdmin) ||
                  isAdmin ? (
                    <div className="gantt-chart-container marketplace-dynamic-container">
                        <div className="main-title-content">
                            <div className="title-breadcrumb">
                                {!isAdmin ? (
                                    <div className="page-title">
                                        <div>
                                            <h3>
                                                Connect Marketplace Dashboard
                                            </h3>
                                            <Breadcrumb>
                                                <BreadcrumbItem>
                                                    <HomeButton />
                                                </BreadcrumbItem>
                                                <BreadcrumbItem active>
                                                    Connect Marketplace
                                                </BreadcrumbItem>
                                            </Breadcrumb>
                                        </div>

                                        <div className="buttons-wrapper">
                                            <ContactSupportButton
                                                supportContext={
                                                    SupportContextEnum.Marketplace
                                                }
                                                buttonType="reference"
                                                buttonText="Contact Administrator"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="page-title">
                                            <h3>
                                                Seller Marketplace Dashboard
                                            </h3>
                                        </div>
                                        <Breadcrumb>
                                            <BreadcrumbItem>
                                                <HomeButton />
                                            </BreadcrumbItem>
                                            <BreadcrumbItem>
                                                <a
                                                    className="breadcrumb-link"
                                                    onClick={() =>
                                                        navigate(
                                                            `/marketplace/admin`
                                                        )
                                                    }>
                                                    Marketplace Admin Home
                                                </a>
                                            </BreadcrumbItem>
                                            <BreadcrumbItem active>
                                                Seller Marketplace
                                            </BreadcrumbItem>
                                        </Breadcrumb>
                                    </div>
                                )}
                            </div>
                        </div>

                        {renderTabs()}
                    </div>
                ) : (
                    <NoPageAccessAlert />
                )}

                {isNdaTermPopupVisible && (
                    <NdaTermsPopup
                        isVisible={isNdaTermPopupVisible}
                        engagementData={currentEngagement.engagementData}
                        engagementProcess={currentEngagement.engagementProcess}
                        engagementId={currentEngagement.id}
                        events={currentEngagement.events}
                        fetchData={() =>
                            fetchEngagementAndAvailableBuyers().then()
                        }
                        activeEventId={activeEventId}
                        setIsVisible={setIsNdaTermPopupVisible}
                        levelOfService={currentEngagement.levelOfService}
                    />
                )}

                {isBuyerSelectionPopupVisible && (
                    <BuyerSelectionPopup
                        isAdminView={isAdmin || isBroker}
                        isVisible={isBuyerSelectionPopupVisible}
                        engagementData={currentEngagement.engagementData}
                        engagementProcess={currentEngagement.engagementProcess}
                        engagementId={currentEngagement.id}
                        events={currentEngagement.events}
                        fetchData={() =>
                            fetchEngagementAndAvailableBuyers().then()
                        }
                        activeEventId={activeEventId}
                        setIsVisible={setIsBuyerSelectionPopupVisible}
                        fteDvms={fteDvms}
                        levelOfService={currentEngagement.levelOfService}
                    />
                )}

                {isTeaserInfoModalVisible && (
                    <TeaserInfoModal
                        isReadOnly={
                            currentEngagement?.engagementData
                                .isTeaserConfirmed ||
                            teaserEvent?.status ===
                                TimelineEventStatus.Completed ||
                            currentEngagement?.engagementProcess?.status ===
                                EngagementProcessStatus.Completed
                        }
                        isVisible={isTeaserInfoModalVisible}
                        onClose={() => setIsTeaserInfoModalVisible(false)}
                        timelineId={currentEngagement.id}
                        teaserData={currentEngagement.engagementData.teaserData}
                        fetchData={() =>
                            fetchEngagementAndAvailableBuyers().then()
                        }
                    />
                )}

                {isRangeOfValueVisible ? (
                    <ValueRangeModal
                        isRangeOfValueVisible={isRangeOfValueVisible}
                        onClose={() => setIsRangeOfValueVisible(false)}
                        onRangeOfValueConfirm={() => {}}
                        selectedEventId={''}
                        isRangeOfValueReadonly={true}
                        minValue={buyerValueRangeInterest.minValue}
                        maxValue={buyerValueRangeInterest.maxValue}
                        isPreviewModeActive={true}
                    />
                ) : (
                    <></>
                )}
            </div>
        );
    };

    const isDeadlineDocketVisible = (tab: any) => {
        return (
            isAdmin ||
            tab.id !== SellerMarketplaceTab.DeadlineDocket ||
            currentEngagement?.engagementProcess?.status !==
                EngagementProcessStatus.Suspended
        );
    };

    const isTabVisibleForTerminatedStatus = (tab: any) => {
        return (
            currentEngagement?.engagementProcess?.status !==
                EngagementProcessStatus.Terminated ||
            tab.id === SellerMarketplaceTab.MyEngagement ||
            tab.id === SellerMarketplaceTab.EngagementDocuments
        );
    };

    const isCommsHubVisible = (tab: any) => {
        return (
            tab.id !== SellerMarketplaceTab.CommsHub ||
            (is28Implemented &&
                getMarketplaceCommsHubTabs(
                    party,
                    currentEngagement.levelOfService,
                    currentEngagement.engagementProcess?.buyers?.some(
                        (b) => b.isDataNdaSigned && b.isActive
                    ) ?? false,
                    !!currentEngagement?.engagementProcess?.broker
                        ?.brokerChatId &&
                        currentEngagement.engagementProcess.broker.isActive
                ).length > 0)
        );
    };

    const isSettingsVisible = (tab: any) => {
        return (
            tab.id !== SellerMarketplaceTab.Settings ||
            (isAdmin &&
                currentEngagement.levelOfService !==
                    LevelOfService.StrategicFinancialAdvisoryService)
        );
    };

    const isSellerProfileVisible = (tab: any) => {
        return (
            tab.id !== SellerMarketplaceTab.SellerProfile ||
            isFF_MrktplcBuyers_3_7Implemented
        );
    };

    const isVDRAccessVisible = (tab: any) => {
        return tab.id !== SellerMarketplaceTab.VDRAccess || isAdmin;
    };
    const isDataRoomVisible = (tab: any) => {
        return (
            tab.id !== SellerMarketplaceTab.DataRoomLayout ||
            isFF_MrktplcBuyers_3_8Enabled
        );
    };

    const shouldShowTab = (tab: any) => {
        return (
            isDeadlineDocketVisible(tab) &&
            isTabVisibleForTerminatedStatus(tab) &&
            isCommsHubVisible(tab) &&
            isSettingsVisible(tab) &&
            isSellerProfileVisible(tab) &&
            isVDRAccessVisible(tab) &&
            isDataRoomVisible(tab)
        );
    };

    return (
        <Layout>
            <div
                className={`connect-marketplace-dashboard ${
                    activeTab === SellerMarketplaceTab.CommsHub
                        ? ' marketplace-comms-hub-container'
                        : ''
                }`}>
                {renderConnectMarketplaceDashboardPage()}
            </div>
            {!isAdmin && currentEngagement && (
                <PaymentValidator
                    fetchEngagement={() =>
                        fetchEngagementAndAvailableBuyers(false).then()
                    }
                />
            )}
        </Layout>
    );
};

export default ConnectMarketplaceDashboard;
